import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, Globe, Send, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Reveal, SectionHeader } from "./Reveal";
import { CONTACT, FAQS, SERVICE_OPTIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { full_name: "", company_name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (form.phone.trim().length < 5) e.phone = "Enter a valid phone number";
    if (!form.service) e.service = "Please select a service";
    if (form.message.trim().length < 5) e.message = "Please add a short message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our team will reach out to you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-jo-primary/12 bg-white px-4 py-3 text-sm text-jo-primary placeholder:text-slate-400 focus:outline-none focus:border-jo-accent focus:ring-2 focus:ring-jo-accent/20 transition-[border,box-shadow]";

  const infoCards = [
    { icon: Phone, label: "Phone", value: CONTACT.phoneDisplay, href: `tel:+${CONTACT.whatsapp}` },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Globe, label: "Website", value: CONTACT.website, href: `https://${CONTACT.website}` },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-white/50 border-t border-jo-primary/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          label="Contact Us"
          title="Let's Build Success Together"
          desc="Have a project in mind? Looking for outsourcing solutions? Our experts are ready to help."
        />

        <div className="mt-14 grid lg:grid-cols-5 gap-10">
          {/* Info + FAQ */}
          <div className="lg:col-span-2 space-y-4">
            {infoCards.map((c) => (
              <Reveal key={c.label}>
                <a
                  href={c.href}
                  data-testid={`contact-${c.label.toLowerCase()}`}
                  className="group flex items-center gap-4 glass rounded-2xl p-5 hover:shadow-lg transition-shadow"
                >
                  <span className="h-12 w-12 rounded-xl bg-gradient-to-br from-jo-primary to-jo-accent flex items-center justify-center text-white shrink-0">
                    <c.icon size={20} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                      {c.label}
                    </div>
                    <div className="text-jo-primary font-semibold group-hover:text-jo-accent transition-colors">
                      {c.value}
                    </div>
                  </div>
                </a>
              </Reveal>
            ))}

            <Reveal>
              <div className="glass rounded-2xl p-6 mt-2">
                <h3 className="text-lg font-semibold text-jo-primary mb-2 flex items-center gap-2">
                  <MapPin size={18} className="text-jo-highlight" /> Frequently Asked
                </h3>
                <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                  {FAQS.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                      <AccordionTrigger className="text-left text-sm font-medium text-jo-primary hover:text-jo-accent">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600 font-light leading-relaxed">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <motion.form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass rounded-3xl p-7 md:p-9"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-jo-primary mb-1.5 block">Full Name *</label>
                  <input
                    data-testid="input-full-name"
                    className={inputCls}
                    placeholder="John Doe"
                    value={form.full_name}
                    onChange={(e) => setField("full_name", e.target.value)}
                  />
                  {errors.full_name && <p role="alert" className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-jo-primary mb-1.5 block">Company Name</label>
                  <input
                    data-testid="input-company"
                    className={inputCls}
                    placeholder="Acme Inc."
                    value={form.company_name}
                    onChange={(e) => setField("company_name", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-jo-primary mb-1.5 block">Email *</label>
                  <input
                    data-testid="input-email"
                    className={inputCls}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                  {errors.email && <p role="alert" className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-jo-primary mb-1.5 block">Phone Number *</label>
                  <input
                    data-testid="input-phone"
                    className={inputCls}
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                  />
                  {errors.phone && <p role="alert" className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs font-semibold text-jo-primary mb-1.5 block">Service Required *</label>
                <Select value={form.service} onValueChange={(v) => setField("service", v)}>
                  <SelectTrigger data-testid="select-service" className={inputCls + " h-auto"}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.service && <p role="alert" className="text-xs text-red-500 mt-1">{errors.service}</p>}
              </div>

              <div className="mt-5">
                <label className="text-xs font-semibold text-jo-primary mb-1.5 block">Message *</label>
                <textarea
                  data-testid="input-message"
                  rows={4}
                  className={inputCls + " resize-none"}
                  placeholder="Tell us about your project or requirements..."
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                />
                {errors.message && <p role="alert" className="text-xs text-red-500 mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-jo-primary text-white font-semibold px-8 py-4 text-sm hover:bg-jo-highlight transition-colors duration-300 disabled:opacity-60 shadow-lg shadow-jo-primary/20"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Submit Enquiry <Send size={16} />
                  </>
                )}
              </button>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
